import { parse } from 'cookie';

/**
 * Retrieves the Atlassian analytics anonymous ID from the browser cookie; returns empty string if not found.
 */
export const getAnalyticsAnonymousId = (): string => {
  return decodeURIComponent(parse(document?.cookie)?.ajs_anonymous_id || '').replace(/['"]+/g, '') || '';
};

/**
 * Transforms a string to kebab case (lowercase with hyphens).
 * Handles various input formats: camelCase, PascalCase, snake_case, spaces, and mixed formats.
 * Removes all non-alphanumeric characters (including apostrophes, punctuation, symbols, etc.).
 *
 * @param str - The input string to transform
 * @returns The string in kebab case format
 *
 * @example
 * toKebabCase('helloWorld') // 'hello-world'
 * toKebabCase('HelloWorld') // 'hello-world'
 * toKebabCase('hello_world') // 'hello-world'
 * toKebabCase('hello world') // 'hello-world'
 * toKebabCase('Hello World') // 'hello-world'
 * toKebabCase('hello-world') // 'hello-world'
 * toKebabCase('HELLO_WORLD') // 'hello-world'
 * toKebabCase("don't") // 'dont'
 * toKebabCase("O'Connor") // 'oconnor'
 * toKebabCase('hello@world') // 'helloworld'
 * toKebabCase('') // ''
 * toKebabCase('hello') // 'hello'
 */
export const toKebabCase = (str: string): string => {
  if (!str || typeof str !== 'string') {
    return '';
  }

  // If the string is all uppercase (with optional underscores, hyphens, or spaces)
  if (/^[A-Z0-9_\s-]+$/.test(str)) {
    // If it's only letters (no numbers), just lowercase it
    if (/^[A-Z]+$/.test(str)) {
      return str.toLowerCase();
    }
    // Otherwise, treat as words separated by underscores, hyphens, or spaces
    return str
      .replace(/_/g, '-')
      .replace(/\s+/g, '-')
      .toLowerCase()
      .replace(/^-+/, '')
      .replace(/-+$/, '')
      .replace(/-+/g, '-');
  }

  return (
    str
      // Handle camelCase and PascalCase by adding hyphen before capital letters
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      // Handle snake_case by replacing underscores with hyphens
      .replace(/_/g, '-')
      // Handle spaces by replacing with hyphens
      .replace(/\s+/g, '-')
      // Remove all non-alphanumeric characters except hyphens
      .replace(/[^a-zA-Z0-9-]/g, '')
      // Convert to lowercase
      .toLowerCase()
      // Remove any leading/trailing hyphens
      .replace(/^-+/, '')
      .replace(/-+$/, '')
      // Replace multiple consecutive hyphens with single hyphen
      .replace(/-+/g, '-')
  );
};
